import { Fragment, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button, Form } from 'reactstrap';
import PropTypes from 'prop-types';
import { useToast, useTranslator, useAppContext } from '@front-activerse/common-components';

import {
  InputTextControlled as InputText,
  InputRadioControlled as InputRadio,
  validateField,
  ValidationErrors,
  RequiredRule,
  UrlFormatRule,
} from '@front-activerse/common-components';

import { sha256 } from 'js-sha256';
import { DEV_ACCESS_FIXTURES, DEV_ACCESS_FIXTURE_STORAGE_KEY } from '../Access/devAccessFixture';

// A badge for the yes/no, then the date it runs to — an expired plan has to be distinguishable from
// one that was never bought, which a bare "no" cannot show.
const AccessValue = ({ flag, accessTo }) => {
  const hasAccess = flag === 'true';
  return (
    <span>
      {/* danger, not secondary: this brand maps bg-secondary to teal, which read as a positive state
          for "no". */}
      <span className={`badge ${hasAccess ? 'bg-success' : 'bg-danger'}`}>
        {hasAccess ? 'yes' : 'no'}
      </span>
      {accessTo ? <span className="ms-1 text-text-variant-color">{accessTo}</span> : null}
    </span>
  );
};

AccessValue.propTypes = {
  flag: PropTypes.string,
  accessTo: PropTypes.string,
};

const DeveloperOptionsComponent = ({ registerAndActivateCoupon, userDataOnDemand }) => {
  const { t, language, changeLanguage } = useTranslator();
  const { toastSuccess } = useToast();
  const [showDeveloperOptions, setShowDeveloperOptions] = useState(false);
  const [apiGateway, setApiGateway] = useState(
    localStorage.getItem('developerOptionsApiUri') || import.meta.env.VITE_REACT_APP_GRAPHQL_URL
  );

  const [errors, setErrors] = useState(new ValidationErrors());
  const [designSystem, setDesignSystem] = useState(
    localStorage.getItem('developerOptionsDesignSystem') || 'brand'
  );

  const [mobileAppMode, setMobileAppMode] = useState(
    localStorage.getItem('developerOptionsMobileAppMode') || 'off'
  );

  // Forces the AI scan flows to act on a chosen failure instead of the real response, so the error
  // screens can be reviewed without having to provoke a rate limit or a bad photo. Read by the
  // scan containers; 'off' leaves the real response untouched.
  const [forcedAiError, setForcedAiError] = useState(
    localStorage.getItem('developerOptionsForcedAiError') || 'off'
  );

  // Replaces the Access page's GraphQL data with a canned variant (Stripe, App Store, BLIK,
  // lifetime…), so every card layout can be reviewed on one account. Read by AccessContainer;
  // 'off' leaves the real response untouched.
  const [accessFixture, setAccessFixture] = useState(
    localStorage.getItem(DEV_ACCESS_FIXTURE_STORAGE_KEY) || 'off'
  );

  const client = useApolloClient();

  const {
    images: { settingsIcon },
    consts: { APP_VERSION, ALLOWED_LANGUAGES },
  } = useAppContext();

  // Read on every render (not in state) so reopening the panel reflects the current session — these
  // are rewritten on each UserData fetch.
  const userEmail = localStorage.getItem('userEmail');
  const userId = localStorage.getItem('userId');
  const dietAccess = sessionStorage.getItem('dietAccess');
  const workoutAccess = sessionStorage.getItem('workoutAccess');
  const aiAccess = sessionStorage.getItem('aiAccess');
  const dietAccessTo = sessionStorage.getItem('dietAccessTo');
  const workoutAccessTo = sessionStorage.getItem('workoutAccessTo');

  const clearApolloCache = () => {
    client.clearStore();
    toastSuccess(<p>{t('developer-options/apollo-cache-cleared')}</p>);
  };

  const clearLocalStorage = () => {
    global.localStorage.clear();
    toastSuccess(<p>{t('developer-options/local-storage-cleared')}</p>);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const removeServiceWorker = () => {
    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }
    toastSuccess(<p>{t('developer-options/service-worker-removed')}</p>);
  };

  const validationRules = {
    apiGateway: [new RequiredRule({ translator: t }), new UrlFormatRule({ translator: t })],
  };

  const validateInput = (field, value) => {
    if (validationRules[field]) {
      setErrors({
        ...errors,
        details: validateField(field, value, validationRules[field], {
          errors,
        }),
      });
    }
  };

  const handleInputChange = (event) => {
    setApiGateway(event.target.value);
    validateInput(event.target.name, event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    global.localStorage.setItem('developerOptionsApiUri', apiGateway);

    toastSuccess(<p>{t('developer-options/api-gateway-changed')}</p>);
  };

  const createNewUser = async () => {
    const hash = sha256(new Date().getTime().toString()).substr(0, 8);

    const request = {
      sex: 'male',
      email: `developeroptionuser+${hash}@activerse.app`,
      password: '1qazxsw2',
      name: 'user created by dev options',
      agreementsTermsAndConditions: true,
      agreementsPersonalDataProcessing: true,
    };

    try {
      const response = await registerAndActivateCoupon(request);
      const { __typename, token } = response.data.auth.register;

      if (__typename === 'AuthRegisterSuccess') {
        const response2 = await userDataOnDemand(token);
        if (response2) {
          const userId = response2.data.me.id;
          const panelUrl = apiGateway.replace('apiv1.', 'panel.').replace('/graphql', '');

          toastSuccess(
            <p>
              {t('developer-options/new-user-created')}.
              <a
                className="btn btn-link ml-2"
                href={`${panelUrl}/user/${userId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('developer-options/add-access')}!
              </a>
            </p>
          );
        }
      } else {
        throw new Error(`Failed to register user from developers options`);
      }
    } catch (e) {
      throw new Error(`Failed to register user from developers options, got error: ${e}`);
    }
  };

  const changeDesignSystem = (system) => {
    setDesignSystem(system);
    global.localStorage.setItem('developerOptionsDesignSystem', system);
    window.location.reload();
  };

  // No reload here (unlike the design-system / mobile-app-mode switches): the scan containers read
  // this at response time, so the next scan already picks the new value up.
  const changeForcedAiError = (value) => {
    setForcedAiError(value);
    if (value === 'off') {
      global.localStorage.removeItem('developerOptionsForcedAiError');
    } else {
      global.localStorage.setItem('developerOptionsForcedAiError', value);
    }
  };

  // The fixture only affects /access, so a pick lands there straight away: a reload when the
  // reviewer is already on the page (like the mobile-app-mode switch), otherwise a navigation.
  const changeAccessFixture = (value) => {
    setAccessFixture(value);
    if (value === 'off') {
      global.localStorage.removeItem(DEV_ACCESS_FIXTURE_STORAGE_KEY);
    } else {
      global.localStorage.setItem(DEV_ACCESS_FIXTURE_STORAGE_KEY, value);
    }
    if (window.location.pathname === '/access') {
      window.location.reload();
    } else {
      window.location.assign('/access');
    }
  };

  const changeMobileAppMode = (mode) => {
    setMobileAppMode(mode);
    if (mode === 'on') {
      global.localStorage.setItem('developerOptionsMobileAppMode', 'on');
    } else {
      global.localStorage.setItem('developerOptionsMobileAppMode', 'off');
    }
    window.location.reload();
  };

  return (
    <div className={showDeveloperOptions ? 'developer-options show' : 'developer-options'}>
      <button
        type="button"
        className="developer-options__button"
        onClick={() => setShowDeveloperOptions(!showDeveloperOptions)}
      >
        <img src={settingsIcon} alt="settings" width={30} />
      </button>
      <div className="developer-options__content">
        <div className="scrollable">
          <h4>{t('developer-options/api-gateway')}</h4>
          <Form onSubmit={handleSubmit} noValidate>
            <InputText
              label={t('developer-options/api-gateway')}
              name="apiGateway"
              value={apiGateway}
              errors={errors}
              handleChange={handleInputChange}
              validationRules={validationRules.apiGateway}
            />

            <Button color="primary" className="px-0 w-100">
              {t('developer-options/set-api-gateway')}
            </Button>
          </Form>
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/language')}</h4>
          <p>
            {t('developer-options/active-language')} {t(`developer-options/language/${language}`)}
          </p>
          {ALLOWED_LANGUAGES.map((lang) => (
            <Button
              key={lang}
              color="primary"
              className="px-0 w-100 mb-2"
              onClick={() => changeLanguage(lang)}
              disabled={language === lang}
            >
              {t(`developer-options/language/${lang}`)}
            </Button>
          ))}
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/clear-data')}</h4>
          <Button color="primary" className="px-0 w-100 mb-2" onClick={clearApolloCache}>
            {t('developer-options/clear-apollo-cache')}
          </Button>
          <Button color="primary" className="px-0 w-100 mb-2" onClick={clearLocalStorage}>
            {t('developer-options/clear-local-storage')}
          </Button>
          <Button color="primary" className="px-0 w-100" onClick={removeServiceWorker}>
            {t('developer-options/remove-service-worker')}
          </Button>
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/create-new-user')}</h4>
          <Button color="primary" className="px-0 w-100" onClick={createNewUser}>
            {t('developer-options/create-new-user')}
          </Button>
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/design-system')}</h4>
          <InputRadio
            id="designSystem1"
            label={t('developer-options/design-system/master')}
            name="designSystem"
            value="master"
            handleChange={() => changeDesignSystem('master')}
            checked={designSystem === 'master'}
            radioRight
          />
          <InputRadio
            id="designSystem2"
            label={t('developer-options/design-system/brand')}
            name="designSystem"
            value="brand"
            handleChange={() => changeDesignSystem('brand')}
            checked={designSystem === 'brand'}
            radioRight
          />
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/mobile-app-mode')}</h4>
          <InputRadio
            id="mobileAppMode1"
            label={t('developer-options/mobile-app-mode/off')}
            name="mobileAppMode"
            value="off"
            handleChange={() => changeMobileAppMode('off')}
            checked={mobileAppMode === 'off'}
            radioRight
          />
          <InputRadio
            id="mobileAppMode2"
            label={t('developer-options/mobile-app-mode/on')}
            name="mobileAppMode"
            value="on"
            handleChange={() => changeMobileAppMode('on')}
            checked={mobileAppMode === 'on'}
            radioRight
          />
          <hr className="my-3" />
          <h4 className="title-small semi-bold">{t('developer-options/forced-ai-error')}</h4>
          {[
            { value: 'off', id: 'forcedAiError0' },
            { value: 'notFood', id: 'forcedAiError1' },
            { value: 'rateLimit', id: 'forcedAiError2' },
            { value: 'aiAccessDenied', id: 'forcedAiError3' },
            { value: 'generic', id: 'forcedAiError4' },
          ].map(({ value, id }) => (
            <InputRadio
              key={value}
              id={id}
              label={t(`developer-options/forced-ai-error/${value}`)}
              name="forcedAiError"
              value={value}
              handleChange={() => changeForcedAiError(value)}
              checked={forcedAiError === value}
              radioRight
            />
          ))}
          <hr className="my-3" />
          {/* Untranslated, like the other technical labels in this panel. */}
          <h4 className="title-small semi-bold">Access page fixture</h4>
          <p className="small text-text-variant-color">
            Replaces the data behind /access so each variant can be reviewed on this account;
            picking one opens the page. Not on production builds.
          </p>
          {DEV_ACCESS_FIXTURES.map((value, index) => (
            <InputRadio
              key={value}
              id={`accessFixture${index}`}
              label={value}
              name="accessFixture"
              value={value}
              handleChange={() => changeAccessFixture(value)}
              checked={accessFixture === value}
              radioRight
            />
          ))}
          <hr className="my-3" />
          {/* Read from the same storage the access gates use, so what shows here is exactly what the
              app acts on. Untranslated, like the other technical labels in this panel. AI has no date:
              the API returns only a boolean for it. */}
          <h4 className="title-small semi-bold">Logged-in user</h4>
          {userEmail ? (
            <div className="small">
              <div className="text-break">
                {userEmail}{' '}
                {userId ? (
                  // Straight to the user in the admin panel, which is where access is checked and
                  // granted. Host comes from the API gateway field (apiv1 -> panel) so it follows
                  // whatever environment the tester pointed the app at, and the locale segment uses
                  // the underscored form the panel routes on. Anything from "?" onwards is dropped
                  // first: a gateway saved with a query string would otherwise leave it stranded in
                  // the middle of the path.
                  <a
                    href={`${apiGateway
                      .split('?')[0]
                      .replace('apiv1.', 'panel.')
                      .replace('/graphql', '')}/${language.replace('-', '_')}/user/${userId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    #{userId}
                  </a>
                ) : (
                  <span className="text-text-variant-color">#—</span>
                )}
              </div>
              {/* A two-column grid rather than a list: the labels line up, so three access rows read
                  as one block instead of three sentences. */}
              <div
                className="mt-1"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: '8px',
                  rowGap: '4px',
                  alignItems: 'center',
                }}
              >
                {[
                  ['Diet', dietAccess, dietAccessTo],
                  ['Workout', workoutAccess, workoutAccessTo],
                  ['AI', aiAccess, null],
                ].map(([label, flag, accessTo]) => (
                  <Fragment key={label}>
                    <span className="text-text-variant-color">{label}</span>
                    <AccessValue flag={flag} accessTo={accessTo} />
                  </Fragment>
                ))}
              </div>
            </div>
          ) : (
            <p className="mb-0 small">Not logged in</p>
          )}

          <hr className="my-3" />
          <p className="mb-0">React app version: {APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
};

DeveloperOptionsComponent.propTypes = {
  registerAndActivateCoupon: PropTypes.func.isRequired,
  userDataOnDemand: PropTypes.func.isRequired,
};

export default DeveloperOptionsComponent;
